import React, { cloneElement, useCallback, useEffect, useRef, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import LGCrudToolbar from "./toolbar";
import LGCrudViewMode from "../../molecules/LGCrud/viewMode";
import LGModal from "../../molecules/LGModal";
import { Button, Menu, MenuItem } from "@mui/material";
import { useSnack } from "../../services/SnakbarProvider";
import { handleError } from "../../services/errorHandler";
import { esES } from "@mui/x-data-grid/locales";
import { usePersistedPagination } from "../../hooks/usePersistedPagination";
import { hasNestedParentId } from "../../services/paginationStorage";

export default function LGModalCrud({ props }) {
  const {
    fetchFunction,
    deleteFunction,
    downloadFunction,
    printFunction,
    data,
    columns,
    addModalData,
    editModalData,
    visualizeModalData,
    actions,
    detailData,
    detailMenu,
    customButtons,
    canEditRow,
    search,
    searchParam = "search",
    searchPlaceholder,
    dataGridProps = {},
    /** When false, page/pageSize changes are not merged into queryParams (avoids refetch on every grid page for client-side pagination). */
    serverDrivenPagination = true,
    paginationKey,
    persistPagination,
  } = props;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const { openSnack } = useSnack();

  // MODAL LOGIC
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState({ header: "", body: <></> });
  const handleModalClose = () => {
    setModalOpen(false);
  };

  // MENU LOGIC
  const [selectedId, setSelectedId] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleOpenDetailClick = (detailMenuItem) => {
    // If onClick is provided, use it instead of opening modal
    if (detailMenuItem.onClick) {
      detailMenuItem.onClick(selectedId);
      handleMenuClose();
      return;
    }
    setModalOpen(true);
    setModalData({
      header: detailMenuItem.header,
      body: cloneElement(detailMenuItem.body, {
        props: { id: selectedId, ...props, onSubmit: handleFormSubmit },
      }),
    });
  };

  // FILTER, SORT AND PAGINATION
  const [queryOptions, setQueryOptions] = useState({});
  const [queryParams, setQueryParams] = useState({ ...data });
  const [searchValue, setSearchValue] = useState("");
  const shouldPersistPagination =
    persistPagination ?? !hasNestedParentId(data);
  const [paginationModel, setPaginationModel] = usePersistedPagination({
    paginationKey,
    persistPagination: shouldPersistPagination,
    defaultPageSize: 20,
  });
  const columnFieldSet = React.useMemo(
    () => new Set((columns ?? []).map((col) => col.field)),
    [columns]
  );

  const onFilterChange = useCallback((filterModel) => {
    setQueryOptions({ filterModel: { ...filterModel } });

    const qp = filterModel.items.reduce((acc, item) => {
      const v = item.value;
      const op = item.operator ?? "";

      if (op === "isEmpty" || op === "isNotEmpty") {
        acc[`${item.field}Operator`] = op;
        return acc;
      }

      if (v === undefined || v === null || v === "") return acc;

      if (Array.isArray(v) && v.length >= 2) {
        const [from, to] = v;
        if (from !== undefined && from !== null && from !== "")
          acc[`${item.field}From`] = from;
        if (to !== undefined && to !== null && to !== "")
          acc[`${item.field}To`] = to;
      } else {
        acc[item.field] = v;
      }

      if (op) acc[`${item.field}Operator`] = op;
      return acc;
    }, {});

    setQueryParams((prev) => {
      const dataKeys = data ? Object.keys(data) : [];
      const staticKeys = new Set([
        "pageSize",
        "pageNumber",
        "search",
        searchParam,
        "sortField",
        "sortDir",
        ...dataKeys.filter((key) => !columnFieldSet.has(key)),
      ]);
      const next = {};
      Object.keys(prev).forEach((key) => {
        if (staticKeys.has(key)) next[key] = prev[key];
      });
      const dataColumnFilters = {};
      dataKeys.forEach((key) => {
        if (!columnFieldSet.has(key)) return;
        if (Object.prototype.hasOwnProperty.call(qp, key)) return;
        const value = data[key];
        if (value === undefined || value === null || value === "") return;
        dataColumnFilters[key] = value;
        const operatorKey = `${key}Operator`;
        if (
          data[operatorKey] !== undefined &&
          data[operatorKey] !== null &&
          data[operatorKey] !== ""
        ) {
          dataColumnFilters[operatorKey] = data[operatorKey];
        }
      });
      return { ...next, ...dataColumnFilters, ...qp };
    });
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [columnFieldSet, data, searchParam]);

  const onSortModelChange = useCallback((sortModel) => {
    setQueryParams((prev) => {
      const next = { ...prev };
      if (sortModel.length > 0 && sortModel[0].field) {
        next.sortField = sortModel[0].field;
        next.sortDir = sortModel[0].sort === "desc" ? "desc" : "asc";
      } else {
        delete next.sortField;
        delete next.sortDir;
      }
      return next;
    });
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  const handlePost = () => {
    setModalOpen(true);
    setModalData({
      header: addModalData.header,
      body: React.cloneElement(addModalData.body, {
        onSubmit: handleFormSubmit,
      }),
    });
  };

  const handleFormSubmit = async () => {
    await fetchData();
    handleModalClose();
  };

  const handleUpdate = (id) => {
    setModalOpen(true);
    setModalData({
      header: editModalData.header,
      body: React.cloneElement(editModalData.body, {
        id,
        onSubmit: handleFormSubmit,
      }),
    });
  };

  const handleFileDownload = (id, row) => {
    downloadFunction(id, row);
  };

  const handlePrint = (id) => {
    printFunction(id);
  };

  const handleVisualize = (id) => {
    if (visualizeModalData) {
      setModalOpen(true);
      setModalData({
        header: visualizeModalData.header,
        body: React.cloneElement(visualizeModalData.body, {
          props: { id, ...props },
        }),
      });
    }
  };

  const handleShowDetailClick = (event, id) => {
    setSelectedId(id);
    setAnchorEl(event.currentTarget);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Seguro quieres eliminar esta entrada?") == true) {
      try {
        const row = rows.find((row) => row.id === id);
        await deleteFunction(id, row);
        setRows(rows.filter((row) => row.id !== id));
        openSnack("Entrada eliminada exitosamente", "success");
      } catch (e) {
        openSnack(handleError(e), "error");
      }
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await fetchFunction(queryParams);
      if (res.data) {
        setRows(res.data);
      }
    } catch (e) {
      openSnack(handleError(e), "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setQueryParams((prev) => {
      const newParams = { ...prev, ...data };
      if (serverDrivenPagination) {
        newParams.pageSize = paginationModel.pageSize;
        newParams.pageNumber = paginationModel.page + 1;
      } else {
        delete newParams.pageSize;
        delete newParams.pageNumber;
      }
      return newParams;
    });
  }, [paginationModel, data, serverDrivenPagination]);

  // Handle search input with debounce. Skip the first run so restored pagination is kept.
  const isFirstSearchEffect = useRef(true);
  useEffect(() => {
    if (isFirstSearchEffect.current) {
      isFirstSearchEffect.current = false;
      return;
    }
    const timeoutId = setTimeout(() => {
      setQueryParams((prev) => {
        const newParams = { ...prev, ...data };
        delete newParams.search;
        delete newParams[searchParam];
        if (searchValue && searchValue.trim() !== "") {
          newParams[searchParam] = searchValue.trim();
        }
        if (serverDrivenPagination) {
          newParams.pageSize = paginationModel.pageSize;
          newParams.pageNumber = 1; // Reset to first page on search
        }
        return newParams;
      });
      // Reset pagination to first page when search changes
      if (serverDrivenPagination) {
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchValue, data, searchParam, serverDrivenPagination]);

  useEffect(() => {
    fetchData();
  }, [queryParams]);

  const [selectedRows, setSelectedRows] = useState([]);

  const handleSelectedRows = (newSelection) => {
    setSelectedRows(newSelection);
  };

  const newColumns = [
    ...columns,
    {
      field: "actions",
      type: "actions",
      headerName: "Acciones",
      width: 250,
      cellClassName: "actions",
      getActions: (params) => {
        const { id, row } = params;
        const canEdit = actions.includes("update") && 
          (canEditRow ? canEditRow(row) : true);
        return [
          <LGCrudViewMode
            id={id}
            onEdit={canEdit ? () => handleUpdate(id) : null}
            onDownload={
              actions.includes("download")
                ? () => handleFileDownload(id, row)
                : null
            }
            onPrint={
              actions.includes("print") ? () => handlePrint(id) : null
            }
            onDelete={
              actions.includes("delete") ? () => handleDelete(id) : null
            }
            onDetail={
              actions.includes("detail")
                ? (event, id) => handleShowDetailClick(event, id)
                : null
            }
            onVisualize={
              actions.includes("visualize") && visualizeModalData
                ? () => handleVisualize(id)
                : null
            }
          />,
        ];
      },
    },
  ];

  return (
    <>
      <LGModal
        open={modalOpen}
        handleClose={handleModalClose}
        header={modalData.header}
      >
        {modalData.body}
      </LGModal>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
      >
        {detailMenu &&
          (typeof detailMenu === 'function'
            ? (() => {
                const row = rows.find(r => r.id === selectedId);
                return row ? detailMenu(row).map((item, index) => (
                  <MenuItem key={index} onClick={() => handleOpenDetailClick(item)}>
                    {item.label}
                  </MenuItem>
                )) : null;
              })()
            : detailMenu.map((item, index) => (
                <MenuItem key={index} onClick={() => handleOpenDetailClick(item)}>
                  {item.label}
                </MenuItem>
              )))}
      </Menu>
      <DataGrid
        rows={rows}
        columns={newColumns}
        loading={loading}
        slots={{
          toolbar:
            actions.includes("create") || actions.includes("select")
              ? LGCrudToolbar
              : null,
        }}
        slotProps={{
          toolbar: {
            handlePost: actions.includes("create") ? handlePost : null,
            customButtons: customButtons?.map((button) => ({
              ...button,
              onClick: () => button.onClick(selectedRows),
            })),
            selectedRows,
            searchValue,
            onSearchChange: setSearchValue,
            search,
            searchPlaceholder,
          },
        }}
        getRowHeight={() => "auto"}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[5, 10, 20, 50, 100, 200]}
        onFilterModelChange={onFilterChange}
        onSortModelChange={onSortModelChange}
        filterMode={serverDrivenPagination ? "server" : "client"}
        sortingMode={serverDrivenPagination ? "server" : "client"}
        paginationMode={serverDrivenPagination ? "server" : "client"}
        {...(serverDrivenPagination ? { rowCount: -1 } : {})}
        // STYLE and i18n
        localeText={esES.components.MuiDataGrid.defaultProps.localeText}
        sx={{
          "& .MuiDataGrid-cell": {
            display: "flex",
            alignItems: "center",
          },
        }}
        // SELECTION
        checkboxSelection={actions.includes("select")}
        onRowSelectionModelChange={handleSelectedRows}
        {...dataGridProps}
      />
    </>
  );
}
